import React, { useState } from 'react';
import { LayoutDependentTabView } from '../../../../v2BaseComponents/TabView';
import { getIntl } from '../../../../../utils/intl';
import OrganizationTabComponent from './OrganizationTab';
import { SideDrawerContainer } from '../../../../v2BaseComponents/SideDrawer';
import OrganizationUsersTab from './OrganizationUsersTab';
import { useIsOrganizationAdmin, useIsOrganizationBillingAdmin } from '../../../../../hooks/api/useOrganization';
import { isPresent } from 'ts-is-present';
import BillingTab from './BillingTab';
import GroupsTab from './GroupsTab';
import InvitationDefinitionsTab from './InvitationDefinitionsTab';
import SubscriptionTab from './SubscriptionTab';
import ClientBillingTab from './ClientBillingTab';
import { RouteProp } from '@react-navigation/native';
import { OrganizationTab, OrganizationTabParamList } from '../../../../../utils/appNavigationNames';
import useFeatureToggles from '../../../../../hooks/api/useFeatureToggles';
import SecuritySettingsTab from './SecuritySettingsTab';
import AdministrationLogsTab from './AdministrationLogsTab';

const intl = getIntl('organization-root-screen', {
    organization: 'Organization',
    groups: 'Groups',
    invitationDefinitions: 'Invitation Templates',
    organizationUsers: 'Users',
    clientInvitations: 'Client Invitations',
    billing: 'Billing',
    subscriptionPlans: 'Subscription Plans',
    clientBilling: 'Client Billing',
    securitySettings: 'Security Settings',
    administrationLogs: 'Administration Logs',
});

export type ResourcesRootScreenOwnProps = {};

export type ResourcesRootScreenProps = ResourcesRootScreenOwnProps & {
    route: RouteProp<OrganizationTabParamList, 'root'>;
};

const ResourcesRootScreen: React.FC<ResourcesRootScreenProps> = ({ route }) => {
    const hasClientInvoicingToggle = !!useFeatureToggles().featureToggles.organizationClientInvoicing;
    const [selectedTab, setSelectedTab] = useState<OrganizationTab>(route.params.defaultTab || 'organization');
    const isBillingAdmin = useIsOrganizationBillingAdmin();
    const isOrganizationAdmin = useIsOrganizationAdmin();

    return (
        <SideDrawerContainer>
            <LayoutDependentTabView
                selectedTab={selectedTab}
                onChange={setSelectedTab as any}
                restrictedWidthVariant="lg"
                tabs={[
                    {
                        title: intl('organization'),
                        renderTabContent: () => <OrganizationTabComponent />,
                        value: 'organization',
                        sideTabContentWidthVariant: 'md' as const,
                    },
                    {
                        title: intl('organizationUsers'),
                        renderTabContent: () => <OrganizationUsersTab />,
                        value: 'organization-users',
                    },
                    {
                        title: intl('groups'),
                        renderTabContent: () => <GroupsTab />,
                        value: 'groups',
                    },
                    {
                        hide: !isOrganizationAdmin,
                        title: intl('invitationDefinitions'),
                        renderTabContent: () => <InvitationDefinitionsTab />,
                        value: 'invitation-definitions',
                    },
                    isBillingAdmin
                        ? {
                              title: intl('subscriptionPlans'),
                              renderTabContent: () => <SubscriptionTab />,
                              value: 'subscription',
                              sideTabContentWidthVariant: 'lg' as const,
                          }
                        : null,
                    isBillingAdmin
                        ? {
                              title: intl('billing'),
                              renderTabContent: () => <BillingTab />,
                              value: 'billing',
                              sideTabContentWidthVariant: 'md' as const,
                          }
                        : null,
                    isBillingAdmin && hasClientInvoicingToggle
                        ? {
                              title: intl('clientBilling'),
                              renderTabContent: () => <ClientBillingTab />,
                              value: 'client-billing',
                              sideTabContentWidthVariant: 'md' as const,
                          }
                        : null,
                    {
                        title: intl('securitySettings'),
                        renderTabContent: () => <SecuritySettingsTab />,
                        value: 'security-settings',
                        sideTabContentWidthVariant: 'md' as const,
                    },
                    isOrganizationAdmin
                        ? {
                              title: intl('administrationLogs'),
                              renderTabContent: () => <AdministrationLogsTab />,
                              value: 'administration-logs',
                              sideTabContentWidthVariant: 'lg' as const,
                          }
                        : null,
                ].filter(isPresent)}
            />
        </SideDrawerContainer>
    );
};

export default React.memo(ResourcesRootScreen);
